import MemberSearchDataHost from "utils/MemberSearchDataHostModel";
import i18n from "../../i18n";
import useTranslation from "react-i18next";

/**
 * @typedef {Function} OnValidateCallback
 * @param {SearchTypeInputDataHost} input
 */

/**
 * @typedef {Object} SearchTypeDataHostData
 * @property {string} label
 * @property {string} value
 * @property {string} type
 * @property {string} placeholder
 * @property {string} value
 * @property {string} appStateKey
 * @property {string} validationErrorMessage
 * @property {OnValidateCallback} onValidate
 */

/**
 * @typedef {Object} MemberSearchDataHostData
 * @property {string} label
 * @property {string} key
 * @property {SearchTypeDataHostData[]} inputs
 */

/**
 * This class is used to manage the member search services for the application.
 * It is a singleton class that is used to add and get services for different partners.
 */
export default class MemberSearchService {
  /**
   * @type {Map<string, MemberSearchDataHost>}
   */
  static serviceMap = new Map();

  /**
   *
   * @param {string} partnerCode
   * @param {MemberSearchDataHostData[]} dataHostConfig
   */
  static addService(partnerCode, dataHostConfig) {
    const dataHost = new MemberSearchDataHost(dataHostConfig);
    this.serviceMap.set(partnerCode, dataHost);
    console.log("MemberSearchService addService: ", this.serviceMap);
  }

  /**
   * Returns the service for the given partner code
   * @param {string} partnerCode
   * @returns {MemberSearchDataHost}
   */
  static getService(partnerCode) {
    console.log("getService ", partnerCode);
    partnerCode = partnerCode || this.getPartnerCode();
    if (!this.serviceMap.has(partnerCode)) {
      console.warn(`Service for partner code ${partnerCode} not found`);
    }
    return this.serviceMap.get(partnerCode);
  }

  /**
   * @description Get the partner code from the URL
   * @returns {string}
   */
  static getPartnerCode() {
    const params = new URLSearchParams(window.location.search);
    if (params && params.has("lg")) {
      return params.get("lg");
    }
    return null;
  }

  /**
   * @description Set the language for the application
   * @param {string} language
   */
  // static setLanguage(language) {
  //   i18n.changeLanguage(language);
  // }

  /**
   * @description Translate the key to the corresponding string
   * @param {string} key
   * @returns {string}
   */
  static t(key) {
    console.log("tr key:", key);
    const i = i18n.t(key);
    console.log("tr:", i);
    return i;
  }
}

const name = "FIMC.ContractSearch.DropdownOptions3";
const firstName = "FIMC.ContractSearch.DropdownSubOptions1";
const firstNameErr = "description.FirstNameRequiredMsg";
const lastName = "FIMC.ContractSearch.DropdownSubOptions2";
const lastNameErr = "description.LastNameRequiredMsg";
const memberNumber = "FIMC.ContractSearch.DropdownOptions1";
const memberNumberErr = "description.MembershipNumberInputErrorMJMC";
const phoneNumber = "FIMC.ContractSearch.DropdownOptions2";
const phoneNumberErr = "description.PhoneNumberRequiredMsg";
const driverLicenseNumber = "description.DriverLicenseNumber";
const driverLicenseNumberErr = "description.DriverLicenseNumberRequiredMsg";
const rentalAgreementNumber = "description.RentalAgreementNumber";
const rentalAgreementNumberErr = "description.RentalAgreementNumberRequiredMsg";
const last8Vin = "description.DropdownOptions1";
const last8VinErr = "description.Last8RequiredMsg";
const policyNumber = "description.PolicyInformationInputPolicyNumber";
const policyNumberErr = "description.PolicyNumberRequiredMsg";
const policyNumberAndVin = "description.PolicyNumberAndVin";

function setupMemberSearchServices() {
  MemberSearchService.addService("MJMC", [
    {
      label: MemberSearchService.t(name),
      key: "NAME",
      inputs: [
        {
          label: MemberSearchService.t(firstName),
          key: "firstName",
          type: "string",
          placeholder: "Enter your first name here",
          value: "",
          appStateKey: "firstName",
          validationErrorMessage: MemberSearchService.t(firstNameErr),
          error: false,
          onValidate: (input) => {
            console.log("First Name validate: ", !!input?.length);
            return !!input?.length;
          },
        },
        {
          label: MemberSearchService.t(lastName),
          key: "lastName",
          type: "string",
          placeholder: "Enter your last name here",
          value: "",
          appStateKey: "lastName",
          error: false,
          validationErrorMessage: MemberSearchService.t(lastNameErr),
          onValidate: (input) => {
            console.log("Last Name validate: ", !!input?.length);
            return !!input?.length;
          },
        },
      ],
    },
    {
      label: MemberSearchService.t(memberNumber),
      key: "MEMBER_NUMBER",
      inputs: [
        {
          label: MemberSearchService.t(memberNumber),
          key: "memberNumber",
          type: "string",
          placeholder: "Enter your member number here",
          value: "",
          appStateKey: "memberNumber",
          error: false,
          validationErrorMessage: MemberSearchService.t(memberNumberErr),
          onValidate: (input) => {
            console.log("Member Number validate: ", input && (input?.length === 5 || input?.length === 6));
            return input && (input?.length === 5 || input?.length === 6);
          },
        },
      ],
    },
  ]);

  MemberSearchService.addService("APA", [
    {
      label: MemberSearchService.t(name),
      key: "NAME",
      selected: true,
      inputs: [
        {
          label: MemberSearchService.t(firstName),
          key: "firstName",
          type: "string",
          placeholder: "Enter your first name here",
          value: "",
          appStateKey: "firstName",
          validationErrorMessage: MemberSearchService.t(firstNameErr),
          error: false,
          onValidate: (input) => {
            console.log("First Name validate: ", !!input?.length);
            return !!input?.length;
          },
        },
        {
          label: MemberSearchService.t(lastName),
          key: "lastName",
          type: "string",
          placeholder: "Enter your last name here",
          value: "",
          appStateKey: "lastName",
          error: false,
          validationErrorMessage: MemberSearchService.t(lastNameErr),
          onValidate: (input) => {
            console.log("Last Name validate: ", !!input?.length);
            return !!input?.length;
          },
        },
      ],
    },
    {
      label: MemberSearchService.t(memberNumber),
      key: "MEMBER_NUMBER",
      selected: false,
      inputs: [
        {
          label: MemberSearchService.t(memberNumber),
          key: "memberNumber",
          type: "string",
          placeholder: "Enter your member number here",
          value: "",
          appStateKey: "memberNumber",
          error: false,
          validationErrorMessage: MemberSearchService.t(memberNumberErr),
          onValidate: (input) => {
            console.log("Member Number validate: ", input && input?.length === 14);
            return input && input?.length === 14;
          },
        },
      ],
    },
    {
      label: MemberSearchService.t(phoneNumber),
      key: "PHONE_NUMBER",
      selected: false,
      inputs: [
        {
          label: MemberSearchService.t(phoneNumber),
          key: "phoneNumber",
          type: "number",
          placeholder: "Enter your phone number here",
          value: "",
          appStateKey: "phoneNumber",
          error: false,
          validationErrorMessage: MemberSearchService.t(phoneNumberErr),
          onValidate: (input) => {
            console.log("Phone Number validate: ", !!input?.length);
            return !!input?.length;
          },
        },
      ],
    },
  ]);
  MemberSearchService.addService("CYSU", [
    {
      label: MemberSearchService.t(name),
      key: "NAME",
      selected: true,
      inputs: [
        {
          label: MemberSearchService.t(firstName),
          key: "firstName",
          type: "string",
          placeholder: "Enter your first name here",
          value: "",
          appStateKey: "firstName",
          validationErrorMessage: MemberSearchService.t(firstNameErr),
          error: false,
          onValidate: (input) => {
            console.log("First Name validate: ", !!input?.length);
            return !!input?.length;
          },
        },
        {
          label: MemberSearchService.t(lastName),
          key: "lastName",
          type: "string",
          placeholder: "Enter your last name here",
          value: "",
          appStateKey: "lastName",
          error: false,
          validationErrorMessage: MemberSearchService.t(lastNameErr),
          onValidate: (input) => {
            console.log("Last Name validate: ", !!input?.length);
            return !!input?.length;
          },
        },
        {
          label: MemberSearchService.t(phoneNumber),
          key: "phoneNumber",
          type: "number",
          placeholder: "Enter your phone number here",
          value: "",
          appStateKey: "phoneNumber",
          error: false,
          validationErrorMessage: MemberSearchService.t(phoneNumberErr),
          onValidate: (input) => {
            console.log("Phone Number validate: ", !!input?.length);
            return !!input?.length;
          },
        },
      ],
    },
    {
      label: MemberSearchService.t(memberNumber),
      key: "MEMBER_NUMBER",
      selected: false,
      inputs: [
        {
          label: MemberSearchService.t(memberNumber),
          key: "memberNumber",
          type: "string",
          placeholder: "Enter your member number here",
          value: "",
          appStateKey: "memberNumber",
          error: false,
          validationErrorMessage: MemberSearchService.t(memberNumberErr),
          onValidate: (input) => {
            console.log("Member Number validate: ", input && input?.length === 14);
            return input && input?.length === 14;
          },
        },
      ],
    },
  ]);

  MemberSearchService.addService("GKW", [
    {
      label: MemberSearchService.t(last8Vin),
      key: "LAST8_VIN",
      selected: true,
      inputs: [
        {
          label: MemberSearchService.t(last8Vin),
          key: "last8Vin",
          type: "string",
          placeholder: "Enter your last 8 of VIN here",
          value: "",
          appStateKey: "last8Vin",
          error: false,
          validationErrorMessage: MemberSearchService.t(last8VinErr),
          onValidate: (input) => {
            console.log("Last 8 VIN validate: ", input && input?.length === 8);
            return input && input?.length === 8;
          },
        },
      ],
    },
    {
      label: MemberSearchService.t(memberNumber),
      key: "MEMBER_NUMBER",
      selected: false,
      inputs: [
        {
          label: MemberSearchService.t(memberNumber),
          key: "memberNumber",
          type: "string",
          placeholder: "Enter your member number here",
          value: "",
          appStateKey: "memberNumber",
          error: false,
          validationErrorMessage: MemberSearchService.t(memberNumberErr),
          onValidate: (input) => {
            console.log("Member Number validate: ", input && input?.length === 14);
            return input && input?.length === 14;
          },
        },
      ],
    },
  ]);

  MemberSearchService.addService("CRE", [
    {
      label: MemberSearchService.t(phoneNumber),
      key: "PHONE_NUMBER",
      selected: false,
      inputs: [
        {
          label: MemberSearchService.t(phoneNumber),
          key: "phoneNumber",
          type: "number",
          placeholder: "Enter your phone number here",
          value: "",
          appStateKey: "phoneNumber",
          error: false,
          validationErrorMessage: MemberSearchService.t(phoneNumberErr),
          onValidate: (input) => {
            console.log("Phone Number validate: ", !!input?.length);
            return !!input?.length;
          },
        },
      ],
    },
    {
      label: MemberSearchService.t(driverLicenseNumber),
      key: "DRIVER_LICENSE_NUMBER",
      inputs: [
        {
          label: MemberSearchService.t(driverLicenseNumber),
          key: "driverLicenseNumber",
          type: "string",
          placeholder: "Enter your driver license number here",
          value: "",
          appStateKey: "driverLicenseNumber",
          error: false,
          validationErrorMessage: MemberSearchService.t(driverLicenseNumberErr),
          onValidate: (input) => {
            return input;
          },
        },
      ],
    },
  ]);

  MemberSearchService.addService("DRIVO", [
    {
      label: MemberSearchService.t(rentalAgreementNumber),
      key: "MEMBER_NUMBER",
      selected: false,
      inputs: [
        {
          label: MemberSearchService.t(rentalAgreementNumber),
          key: "memberNumber",
          type: "string",
          placeholder: "Enter your rental agreement number here",
          value: "",
          appStateKey: "memberNumber",
          error: false,
          validationErrorMessage: MemberSearchService.t(rentalAgreementNumberErr),
          onValidate: (input) => {
            return input;
          },
        },
      ],
    },
    {
      label: MemberSearchService.t(last8Vin),
      key: "LAST8_VIN",
      inputs: [
        {
          label: MemberSearchService.t(last8Vin),
          key: "last8Vin",
          type: "string",
          placeholder: "Enter your last 8 of VIN here",
          value: "",
          appStateKey: "last8Vin",
          error: false,
          validationErrorMessage: MemberSearchService.t(last8VinErr),
          onValidate: (input) => {
            console.log("Last 8 VIN validate: ", input && input?.length === 8);
            return input && input?.length === 8;
          },
        },
        {
          label: MemberSearchService.t(lastName),
          key: "lastName",
          type: "string",
          placeholder: "Enter your last name here",
          value: "",
          appStateKey: "lastName",
          error: false,
          validationErrorMessage: MemberSearchService.t(lastNameErr),
          onValidate: (input) => {
            console.log("Last Name validate: ", !!input?.length);
            return !!input?.length;
          },
        },
      ],
    },
  ]);
  MemberSearchService.addService("MER", [
    {
      label: MemberSearchService.t(policyNumberAndVin),
      key: "POLICY_NUMBER",
      selected: false,
      inputs: [
        {
          label: MemberSearchService.t(policyNumber),
          key: "memberNumber",
          type: "string",
          value: "",
          appStateKey: "memberNumber",
          error: false,
          validationErrorMessage: MemberSearchService.t(policyNumberErr),
          onValidate: (input) => {
            return input;
          },
        },
        {
          label: MemberSearchService.t(last8Vin),
          key: "last8Vin",
          type: "string",
          placeholder: "Enter your last 8 of VIN here",
          value: "",
          appStateKey: "last8Vin",
          error: false,
          validationErrorMessage: MemberSearchService.t(last8VinErr),
          onValidate: (input) => {
            return input && input?.length === 8;
          },
        },
      ],
    },
  ]);
}

i18n.on("initialized", setupMemberSearchServices);
